import {inject} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  type CanActivateFn,
  type GuardResult,
  type MaybeAsync,
  type RedirectFunction,
  Router,
  RouterStateSnapshot
} from '@angular/router';
import {of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {Logger} from '@app/utils';
import {AuthService} from './auth.service';

const logger: Logger = Logger.get('AuthGuard');

const LOGIN_ROUTE = '/login';

export const authGuard: CanActivateFn = (
  _: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): MaybeAsync<GuardResult> => {
  const router = inject(Router);
  const authService = inject(AuthService);

  logger.debug('AuthGuard - Check if allowed to navigate to:', state.url);

  if (authService.currentAuthenticatedUser) {
    logger.debug('AuthGuard - User already authenticated');
    return true;
  }

  return authService.authenticateUser().pipe(
    map(_ => {
      logger.debug('AuthGuard - User authenticated successfully');
      return true;
    }),
    catchError(_ => {
      logger.debug('AuthGuard - Authentication failed, attempting to refresh token...');
      return authService.refreshToken().pipe(
        switchMap(_ => {
          logger.debug('AuthGuard - Token refreshed successfully, reattempting authentication...');
          return authService.authenticateUser();
        }),
        map(_ => {
          logger.debug('AuthGuard - User authenticated after token refresh');
          return true;
        })
      );
    }),
    catchError(_ => {
      logger.debug('AuthGuard - Token refresh failed or User still not authenticated after token refresh, redirecting to login');
      return of(router.parseUrl(`${LOGIN_ROUTE}?next=${encodeURIComponent(state.url)}`));
    }),
  );
};

export const afterAuthRootRedirect: RedirectFunction = _ => {
  const authService = inject(AuthService);
  if (authService.currentAuthenticatedUser?.role === 'rng_user') {
    return 'rng';
  }
  return 'games';
}
